import React, { useEffect, useMemo, useState } from "react";
import { formatPrice } from "../lib/format";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";

function getOrCreateCartId() {
  try {
    let id = localStorage.getItem("berulia_cart_id");
    if (!id) {
      id = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem("berulia_cart_id", id);
    }
    return id;
  } catch {
    return `${Date.now()}`;
  }
}

const empty = { line1: "", line2: "", postal_code: "", city: "", country: "France" };

const SHIPPING_RATES = { mondial_relay: 4.90, colissimo: 6.90 };

const F = ({ label, value, onChange, type = "text", required = true, testid }) => (
  <label className="block">
    <span className="overline text-berulia-inkmuted">{label}</span>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="mt-1 w-full bg-white border border-berulia-border px-3 py-2 focus:border-berulia-ink outline-none"
    />
  </label>
);

export default function Checkout() {
  const { items, subtotal, clear, freeShippingThreshold } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState(empty);
  const [shippingAddr, setShippingAddr] = useState({ ...empty, recipient_name: "" });
  const [sameAddr, setSameAddr] = useState(true);
  const [carrier, setCarrier] = useState("colissimo");
  const [loading, setLoading] = useState(false);
  // Sign-out flow phases:
  //   ""        → choice screen (login or guest)
  //   "guest"   → email + identity form (sends verification link)
  //   "sent"    → "check your email" screen
  const [mode, setMode] = useState("");
  const [guest, setGuest] = useState({ email: "", first_name: "", last_name: "", phone: "" });
  const [guestAddr, setGuestAddr] = useState({ line1: "", line2: "", postal_code: "", city: "", country: "France" });
  const [guestSending, setGuestSending] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.billing_address) {
      setBilling({ ...empty, ...user.billing_address });
    }
    if (user.shipping_address) {
      setShippingAddr({ ...empty, recipient_name: "", ...user.shipping_address });
      setSameAddr(false);
    }
  }, [user]);

  const freeShipping = subtotal >= freeShippingThreshold;
  const shippingFor = (c) => freeShipping ? 0 : SHIPPING_RATES[c];
  const shipping = shippingFor(carrier);
  const total = subtotal + shipping;
  const cartId = useMemo(() => getOrCreateCartId(), []);

  useEffect(() => {
    if (!user || items.length === 0) return;
    const t = setTimeout(() => {
      api.post("/cart/snapshot", {
        cart_id: cartId,
        customer_email: user.email,
        customer_name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        items: items.map((i) => ({
          product_id: i.product_id,
          name: i.name,
          image: i.image,
          size: i.size,
          quantity: i.quantity,
          unit_price: i.unit_price,
        })),
        subtotal,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [user, items, subtotal, cartId]);

  if (items.length === 0) return <div className="py-24 text-center"><p className="font-heading text-3xl">Votre panier est vide.</p></div>;

  if (!user && !mode) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <p className="overline text-berulia-rosedeep">Finaliser la commande</p>
        <h1 className="font-heading text-4xl mt-3">Comment souhaitez-vous continuer ?</h1>
        <div className="mt-10 grid gap-4">
          <button
            data-testid="checkout-login"
            onClick={()=>navigate("/connexion?next=/checkout")}
            className="bg-berulia-rosedeep text-berulia-inkdeep overline px-8 py-5 hover:bg-berulia-rose transition">
            Se connecter / créer un compte →
          </button>
          <button
            data-testid="checkout-guest"
            onClick={()=>setMode("guest")}
            className="border border-berulia-border text-berulia-ink overline px-8 py-5 hover:bg-white transition">
            Continuer en invitée →
          </button>
        </div>
        <p className="mt-6 text-xs text-berulia-inkmuted">En mode invitée, nous vous envoyons un lien de vérification par email avant de finaliser votre commande.</p>
      </div>
    );
  }

  const sendGuestLink = async (e) => {
    e.preventDefault();
    setGuestSending(true);
    try {
      await api.post("/auth/guest-start", {
        email: guest.email.trim().toLowerCase(),
        first_name: guest.first_name.trim(),
        last_name: guest.last_name.trim(),
        phone: guest.phone.trim(),
        billing_address: {
          line1: guestAddr.line1.trim(),
          line2: guestAddr.line2.trim(),
          postal_code: guestAddr.postal_code.trim(),
          city: guestAddr.city.trim(),
          country: guestAddr.country || "France",
        },
        next: "/checkout",
      });
      setMode("sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'envoyer le lien — réessayez.");
    } finally {
      setGuestSending(false);
    }
  };

  if (!user && mode === "guest") {
    return (
      <div className="max-w-xl mx-auto px-6 py-16">
        <button onClick={()=>setMode("")} className="overline link-underline text-sm">← Retour</button>
        <h1 className="font-heading text-4xl mt-4">Commander en <span className="font-serif-italic">invitée</span></h1>
        <p className="mt-3 text-sm text-berulia-inkmuted">Renseignez vos coordonnées : nous vous envoyons un lien de vérification pour confirmer votre email, puis vous pourrez finaliser votre commande.</p>
        <form onSubmit={sendGuestLink} className="mt-8 grid sm:grid-cols-2 gap-4">
          <F label="Email" type="email" value={guest.email} onChange={v=>setGuest({...guest, email:v})} testid="guest-email"/>
          <F label="Téléphone" type="tel" value={guest.phone} onChange={v=>setGuest({...guest, phone:v})} testid="guest-phone"/>
          <F label="Prénom" value={guest.first_name} onChange={v=>setGuest({...guest, first_name:v})} testid="guest-first-name"/>
          <F label="Nom" value={guest.last_name} onChange={v=>setGuest({...guest, last_name:v})} testid="guest-last-name"/>
          <div className="sm:col-span-2 pt-2">
            <p className="overline text-berulia-inkmuted">Adresse de livraison</p>
          </div>
          <div className="sm:col-span-2">
            <F label="Adresse" value={guestAddr.line1} onChange={v=>setGuestAddr({...guestAddr, line1:v})} testid="guest-line1"/>
          </div>
          <div className="sm:col-span-2">
            <F label="Complément" value={guestAddr.line2} onChange={v=>setGuestAddr({...guestAddr, line2:v})} required={false} testid="guest-line2"/>
          </div>
          <F label="Code postal" value={guestAddr.postal_code} onChange={v=>setGuestAddr({...guestAddr, postal_code:v})} testid="guest-postal"/>
          <F label="Ville" value={guestAddr.city} onChange={v=>setGuestAddr({...guestAddr, city:v})} testid="guest-city"/>
          <button type="submit" disabled={guestSending} data-testid="guest-send"
            className="sm:col-span-2 mt-2 bg-berulia-rosedeep text-berulia-inkdeep overline py-4 hover:bg-berulia-rose transition disabled:opacity-50">
            {guestSending ? "Envoi…" : "Recevoir le lien de vérification →"}
          </button>
        </form>
      </div>
    );
  }

  if (!user && mode === "sent") {
    return (
      <div className="max-w-xl mx-auto px-6 py-20 text-center">
        <p className="font-serif-italic text-3xl text-berulia-rosedeep">Vérifiez votre boîte mail ✿</p>
        <h1 className="font-heading text-4xl mt-4">Un lien vous attend</h1>
        <p className="mt-6 text-berulia-inkmuted">Nous venons d'envoyer un lien de vérification à <strong>{guest.email}</strong>. Cliquez dessus depuis votre boîte mail pour revenir ici et finaliser votre commande — votre panier vous attend.</p>
        <p className="mt-6 text-xs text-berulia-inkmuted">Pas reçu ? Vérifiez vos spams ou <button type="button" onClick={()=>setMode("guest")} className="underline">renvoyer le lien</button>.</p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ship = sameAddr ? billing : shippingAddr;
      const r = await api.post("/orders", {
        items: items.map(i => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
        billing_address: billing,
        shipping_address: ship,
        carrier,
        customer_note: "",
        cart_id: cartId,
      });
      window.location.href = r.data.payment_url;
    } catch (err) {
      // The backend returns 409 with a structured `detail` when one or
      // more cart items are out of stock or unavailable. Surface a clear
      // per-line message so the customer can fix the cart instead of a
      // generic "Erreur".
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.issues) {
        const REASON_FR = {
          out_of_stock: "épuisé",
          insufficient_stock: "stock insuffisant",
          inactive: "indisponible",
          not_found: "introuvable",
        };
        const lines = detail.issues.map(i => {
          const reason = REASON_FR[i.reason] || i.reason;
          if (i.reason === "insufficient_stock") {
            return `${i.name} taille ${i.size} — ${reason} (reste ${i.available}, demandé ${i.requested})`;
          }
          return `${i.name} taille ${i.size} — ${reason}`;
        });
        toast.error(detail.message + "\n• " + lines.join("\n• "), { duration: 10_000 });
      } else {
        const msg = typeof detail === "string" ? detail : "Erreur lors de la création de la commande";
        toast.error(msg);
      }
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-16 grid lg:grid-cols-3 gap-12">
      <form onSubmit={submit} className="lg:col-span-2 space-y-10">
        <div>
          <h2 className="font-heading text-3xl mb-2">Vos coordonnées</h2>
          <p className="text-sm text-berulia-inkmuted mb-4">Commande passée en tant que <strong>{user.first_name} {user.last_name}</strong> · {user.email}</p>
          <Link to="/mon-compte" className="overline link-underline text-sm">Modifier mes infos →</Link>
        </div>

        <div>
          <h2 className="font-heading text-3xl mb-6">Adresse de facturation</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Adresse" value={billing.line1} onChange={v=>setBilling({...billing, line1:v})} testid="billing-line1"/>
            <F label="Complément" value={billing.line2} onChange={v=>setBilling({...billing, line2:v})} required={false} testid="billing-line2"/>
            <F label="Code postal" value={billing.postal_code} onChange={v=>setBilling({...billing, postal_code:v})} testid="billing-postal"/>
            <F label="Ville" value={billing.city} onChange={v=>setBilling({...billing, city:v})} testid="billing-city"/>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={sameAddr} onChange={e=>setSameAddr(e.target.checked)} data-testid="same-address-checkbox"/>
            Utiliser cette adresse pour la livraison
          </label>
          {sameAddr && (
            <p className="mt-2 text-xs text-berulia-inkmuted">Pour offrir, décochez pour livrer à une autre adresse.</p>
          )}
        </div>

        {!sameAddr && (
          <div>
            <h2 className="font-heading text-3xl mb-2">Adresse de livraison</h2>
            <p className="text-sm text-berulia-inkmuted mb-6">Indiquez l'adresse — et le nom du destinataire si c'est un cadeau ✿</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Destinataire (pour un cadeau)" value={shippingAddr.recipient_name || ""} onChange={v=>setShippingAddr({...shippingAddr, recipient_name:v})} required={false} testid="shipping-recipient"/>
              <span className="hidden sm:block"/>
              <F label="Adresse" value={shippingAddr.line1} onChange={v=>setShippingAddr({...shippingAddr, line1:v})} testid="shipping-line1"/>
              <F label="Complément" value={shippingAddr.line2} onChange={v=>setShippingAddr({...shippingAddr, line2:v})} required={false} testid="shipping-line2"/>
              <F label="Code postal" value={shippingAddr.postal_code} onChange={v=>setShippingAddr({...shippingAddr, postal_code:v})} testid="shipping-postal"/>
              <F label="Ville" value={shippingAddr.city} onChange={v=>setShippingAddr({...shippingAddr, city:v})} testid="shipping-city"/>
            </div>
          </div>
        )}

        <div>
          <h2 className="font-heading text-3xl mb-6">Mode de livraison</h2>
          <div className="space-y-3">
            <label className={`flex items-center justify-between gap-4 border p-4 cursor-pointer ${carrier==="colissimo" ? "border-berulia-ink" : "border-berulia-border"}`}>
              <div className="flex items-center gap-3"><input type="radio" name="carrier" value="colissimo" checked={carrier==="colissimo"} onChange={()=>setCarrier("colissimo")} data-testid="carrier-colissimo"/>
                <div><p className="font-heading text-lg">Colissimo · Domicile</p><p className="text-xs text-berulia-inkmuted">2–3 jours ouvrés</p></div>
              </div>
              <p className="overline">{shippingFor("colissimo")===0?"Offerte":formatPrice(shippingFor("colissimo"))}</p>
            </label>
            <label className={`flex items-center justify-between gap-4 border p-4 cursor-pointer ${carrier==="mondial_relay" ? "border-berulia-ink" : "border-berulia-border"}`}>
              <div className="flex items-center gap-3"><input type="radio" name="carrier" value="mondial_relay" checked={carrier==="mondial_relay"} onChange={()=>setCarrier("mondial_relay")} data-testid="carrier-mondial-relay"/>
                <div><p className="font-heading text-lg">Mondial Relay · Point relais</p><p className="text-xs text-berulia-inkmuted">3–5 jours ouvrés</p></div>
              </div>
              <p className="overline">{shippingFor("mondial_relay")===0?"Offerte":formatPrice(shippingFor("mondial_relay"))}</p>
            </label>
          </div>
        </div>

        <button type="submit" disabled={loading} data-testid="place-order-btn"
          className="w-full bg-berulia-rosedeep text-berulia-inkdeep overline py-5 hover:bg-berulia-rose transition disabled:opacity-50">
          {loading ? "Redirection vers le paiement…" : "Procéder au paiement →"}
        </button>
        <p className="text-xs text-berulia-inkmuted">Paiement sécurisé via Stripe. Vous serez redirigée pour finaliser le paiement.</p>
      </form>

      <aside className="bg-white border border-berulia-border p-6 h-fit">
        <h3 className="font-heading text-2xl mb-4">Récapitulatif</h3>
        <div className="space-y-3 max-h-72 overflow-y-auto">
          {items.map(it => (
            <div key={`${it.product_id}-${it.size}`} className="flex gap-3 items-start">
              <img src={it.image} className="w-14 h-18 object-cover bg-berulia-beigewarm" alt=""/>
              <div className="flex-1 text-sm">
                <p className="font-heading text-base">{it.name}</p>
                <p className="text-xs text-berulia-inkmuted">{it.size} × {it.quantity}</p>
              </div>
              <p className="text-sm">{formatPrice((it.unit_price*it.quantity))}</p>
            </div>
          ))}
        </div>
        <div className="border-t border-berulia-border mt-4 pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span>Sous-total</span><span>{formatPrice(subtotal)}</span></div>
          <div className="flex justify-between"><span>Livraison</span><span>{shipping===0?"Offerte":formatPrice(shipping)}</span></div>
          <div className="flex justify-between font-heading text-xl pt-2 border-t border-berulia-border"><span>Total</span><span>{formatPrice(total)}</span></div>
        </div>
      </aside>
    </div>
  );
}
